/**
 * DOS518 - Digital Operations System for Wildfire Management
 * 
 * Copyright (c) 2024-2025 Angelo Irtilaç
 * All rights reserved.
 * 
 * This software is proprietary and confidential.
 * Unauthorized copying, distribution, modification, or use is strictly prohibited.
 * 
 * ---
 * 
 * Author: Angelo Irtilaç
 * Qualifications: DOS (Direttore delle Operazioni di Spegnimento)
 *                39 years - Corpo Nazionale dei Vigili del Fuoco
 *                DOS Trainer & Instructor
 * 
 * Location: Carpi, Italy
 * Contact: [inserire email]
 * 
 * ---
 * 
 * This software implements official Italian Fire Department protocols:
 * - C.O.A.U. (Centro Operativo Aereo Unificato)
 * - SI.TA.C. (Sistema Tattico di Coordinamento)
 * - L.A.C.E.S. (Lookout, Awareness, Communication, Escape routes, Safety zones)
 * 
 * Developed with operational expertise acquired through decades of wildfire
 * emergency management and coordination of suppression operations.
 * 
 * For commercial licensing inquiries, please contact the author.
 */
// ============================================
// DOS518 - Fraseologia ICAO Utilities
// Alfabeto fonetico e template comunicazioni
// ============================================

// Alfabeto fonetico NATO/ICAO
export const ALFABETO_FONETICO: Record<string, string> = {
  A: 'ALFA',
  B: 'BRAVO',
  C: 'CHARLIE',
  D: 'DELTA',
  E: 'ECHO',
  F: 'FOXTROT',
  G: 'GOLF',
  H: 'HOTEL',
  I: 'INDIA',
  J: 'JULIETT',
  K: 'KILO',
  L: 'LIMA',
  M: 'MIKE',
  N: 'NOVEMBER',
  O: 'OSCAR',
  P: 'PAPA',
  Q: 'QUEBEC',
  R: 'ROMEO',
  S: 'SIERRA',
  T: 'TANGO',
  U: 'UNIFORM',
  V: 'VICTOR',
  W: 'WHISKEY',
  X: 'XRAY',
  Y: 'YANKEE',
  Z: 'ZULU',
  '0': 'ZERO',
  '1': 'UNO',
  '2': 'DUE',
  '3': 'TRE',
  '4': 'QUATTRO',
  '5': 'CINQUE',
  '6': 'SEI',
  '7': 'SETTE',
  '8': 'OTTO',
  '9': 'NOVE',
};

// Converti testo in spelling fonetico
export const toPhonetic = (text: string): string => {
  return text
    .toUpperCase()
    .split('')
    .map((char) => ALFABETO_FONETICO[char] || char)
    .join(' ');
};

// Converti callsign in fonetico
export const callsignToPhonetic = (callsign: string): string => {
  return callsign
    .toUpperCase()
    .split('')
    .map((char) => ALFABETO_FONETICO[char] || char)
    .join('-');
};

// Converti sigla mezzo in fonetico ICAO solo se ≤ 3 caratteri alfanumerici.
// Sigla più lunghe (es. "DRAGO-101", "NIBBIO") vengono usate così come sono.
export const toPhoneticCallsign = (sigla: string): string => {
  const alphaNum = sigla.replace(/[^A-Za-z0-9]/g, '');
  if (alphaNum.length > 3) return sigla.toUpperCase();
  return toPhonetic(sigla);
};

// Template messaggi standard AIB
export interface MessageTemplate {
  id: string;
  categoria: string;
  titolo: string;
  template: string;
  variabili: string[];
  esempio: string;
}

export const TEMPLATES_MESSAGGI: MessageTemplate[] = [
  // === AUTORIZZAZIONI DECOLLO ===
  {
    id: 'auth_decollo',
    categoria: 'Autorizzazioni',
    titolo: 'Autorizzazione Decollo',
    template: '{CALLSIGN}, DOS {CODICE_INTERVENTO}, autorizzato decollo per intervento AIB località {LOCALITA}',
    variabili: ['CALLSIGN', 'CODICE_INTERVENTO', 'LOCALITA'],
    esempio: 'INDIA 45, DOS Alfa 123, autorizzato decollo per intervento AIB località Monte Rosso',
  },
  {
    id: 'auth_avvicinamento',
    categoria: 'Autorizzazioni',
    titolo: 'Autorizzazione Avvicinamento',
    template: '{CALLSIGN}, autorizzato avvicinamento zona operativa, quota minima {QUOTA} piedi, vento da {DIREZIONE_VENTO} a {VELOCITA_VENTO} nodi',
    variabili: ['CALLSIGN', 'QUOTA', 'DIREZIONE_VENTO', 'VELOCITA_VENTO'],
    esempio: 'INDIA 45, autorizzato avvicinamento zona operativa, quota minima 500 piedi, vento da nord-ovest a 15 nodi',
  },

  // === COMUNICAZIONI SGANCIO ===
  {
    id: 'sgancio_richiesta',
    categoria: 'Sgancio',
    titolo: 'Richiesta Sgancio',
    template: '{CALLSIGN}, richiedete sgancio su coordinate {LAT} {LNG}, target {TARGET}',
    variabili: ['CALLSIGN', 'LAT', 'LNG', 'TARGET'],
    esempio: 'INDIA 45, richiedete sgancio su coordinate 41.9028 Nord 12.4964 Est, target fronte sud',
  },
  {
    id: 'sgancio_autorizzato',
    categoria: 'Sgancio',
    titolo: 'Sgancio Autorizzato',
    template: '{CALLSIGN}, sgancio autorizzato, personale in sicurezza, via libera sgancio',
    variabili: ['CALLSIGN'],
    esempio: 'INDIA 45, sgancio autorizzato, personale in sicurezza, via libera sgancio',
  },
  {
    id: 'sgancio_negato',
    categoria: 'Sgancio',
    titolo: 'Sgancio Negato',
    template: '{CALLSIGN}, NEGATIVO sgancio, {MOTIVO}, attendere istruzioni',
    variabili: ['CALLSIGN', 'MOTIVO'],
    esempio: 'INDIA 45, NEGATIVO sgancio, personale in zona, attendere istruzioni',
  },
  {
    id: 'sgancio_completato',
    categoria: 'Sgancio',
    titolo: 'Sgancio Completato',
    template: 'DOS {CODICE_INTERVENTO}, {CALLSIGN}, sgancio completato, {LITRI} litri, in rotta per {DESTINAZIONE}',
    variabili: ['CODICE_INTERVENTO', 'CALLSIGN', 'LITRI', 'DESTINAZIONE'],
    esempio: 'DOS Alfa 123, INDIA 45, sgancio completato, 6000 litri, in rotta per rifornimento',
  },

  // === EMERGENZE ===
  {
    id: 'mayday',
    categoria: 'Emergenza',
    titolo: 'MAYDAY',
    template: 'MAYDAY MAYDAY MAYDAY, {CALLSIGN}, {TIPO_EMERGENZA}, posizione {LAT} {LNG}, {PERSONE} persone a bordo, carburante per {MINUTI} minuti',
    variabili: ['CALLSIGN', 'TIPO_EMERGENZA', 'LAT', 'LNG', 'PERSONE', 'MINUTI'],
    esempio: 'MAYDAY MAYDAY MAYDAY, INDIA 45, motore in avaria, posizione 41.9028 Nord 12.4964 Est, 3 persone a bordo, carburante per 45 minuti',
  },
  {
    id: 'pan_pan',
    categoria: 'Emergenza',
    titolo: 'PAN PAN',
    template: 'PAN PAN PAN PAN PAN PAN, {CALLSIGN}, {TIPO_URGENZA}, richiedo {RICHIESTA}',
    variabili: ['CALLSIGN', 'TIPO_URGENZA', 'RICHIESTA'],
    esempio: 'PAN PAN PAN PAN PAN PAN, INDIA 45, strumentazione parzialmente inoperativa, richiedo rientro anticipato',
  },
  {
    id: 'evacuazione',
    categoria: 'Emergenza',
    titolo: 'Evacuazione Immediata',
    template: 'ATTENZIONE TUTTE LE UNITÀ, evacuazione immediata zona {ZONA}, ripiegamento su punto raccolta {PUNTO}, confermare ricezione',
    variabili: ['ZONA', 'PUNTO'],
    esempio: 'ATTENZIONE TUTTE LE UNITÀ, evacuazione immediata zona Alfa, ripiegamento su punto raccolta Bravo, confermare ricezione',
  },

  // === COORDINAMENTO TERRESTRE ===
  {
    id: 'pos_report',
    categoria: 'Coordinamento',
    titolo: 'Rapporto Posizione',
    template: 'DOS {CODICE_INTERVENTO}, squadra {SQUADRA}, in posizione {POSIZIONE}, {STATO}',
    variabili: ['CODICE_INTERVENTO', 'SQUADRA', 'POSIZIONE', 'STATO'],
    esempio: 'DOS Alfa 123, squadra 1, in posizione fronte nord, operativi attacco diretto',
  },
  {
    id: 'richiesta_mezzo',
    categoria: 'Coordinamento',
    titolo: 'Richiesta Mezzo',
    template: 'DOS {CODICE_INTERVENTO}, richiedo {TIPO_MEZZO} in posizione {POSIZIONE} per {MOTIVAZIONE}',
    variabili: ['CODICE_INTERVENTO', 'TIPO_MEZZO', 'POSIZIONE', 'MOTIVAZIONE'],
    esempio: 'DOS Alfa 123, richiedo autobotte in posizione bivio quota 450 per rifornimento',
  },
  {
    id: 'cambio_turno',
    categoria: 'Coordinamento',
    titolo: 'Cambio Turno DOS',
    template: 'Attenzione tutte le unità, cambio DOS in corso, nuovo DOS {NUOVO_DOS}, frequenza operativa invariata',
    variabili: ['NUOVO_DOS'],
    esempio: 'Attenzione tutte le unità, cambio DOS in corso, nuovo DOS Rossi Mario, frequenza operativa invariata',
  },

  // === LACES ===
  {
    id: 'vedetta_attiva',
    categoria: 'LACES',
    titolo: 'Vedetta Attiva',
    template: 'DOS {CODICE_INTERVENTO}, vedetta {NOME_VEDETTA} attiva in posizione {POSIZIONE}, visibilità {VISIBILITA}',
    variabili: ['CODICE_INTERVENTO', 'NOME_VEDETTA', 'POSIZIONE', 'VISIBILITA'],
    esempio: 'DOS Alfa 123, vedetta Alfa attiva in posizione crinale nord, visibilità buona',
  },
  {
    id: 'via_fuga',
    categoria: 'LACES',
    titolo: 'Comunicazione Via di Fuga',
    template: 'Attenzione squadra {SQUADRA}, via di fuga assegnata: {DESCRIZIONE_VIA}, punto raccolta {PUNTO_RACCOLTA}',
    variabili: ['SQUADRA', 'DESCRIZIONE_VIA', 'PUNTO_RACCOLTA'],
    esempio: 'Attenzione squadra 1, via di fuga assegnata: strada forestale direzione sud, punto raccolta piazzale chiesa',
  },

  // === METEO ===
  {
    id: 'aggiornamento_meteo',
    categoria: 'Meteo',
    titolo: 'Aggiornamento Meteo',
    template: 'Attenzione tutte le unità, aggiornamento meteo: vento {DIREZIONE} {INTENSITA} km/h, temperatura {TEMP}°C, umidità {UMIDITA}%',
    variabili: ['DIREZIONE', 'INTENSITA', 'TEMP', 'UMIDITA'],
    esempio: 'Attenzione tutte le unità, aggiornamento meteo: vento nord-ovest 25 km/h, temperatura 32°C, umidità 35%',
  },

  // === FINE OPERAZIONI ===
  {
    id: 'fine_intervento_aereo',
    categoria: 'Fine Operazioni',
    titolo: 'Fine Intervento Aereo',
    template: '{CALLSIGN}, DOS {CODICE_INTERVENTO}, termine intervento aereo, autorizzato rientro base, grazie per la collaborazione',
    variabili: ['CALLSIGN', 'CODICE_INTERVENTO'],
    esempio: 'INDIA 45, DOS Alfa 123, termine intervento aereo, autorizzato rientro base, grazie per la collaborazione',
  },
  {
    id: 'chiusura_operazione',
    categoria: 'Fine Operazioni',
    titolo: 'Chiusura Operazione',
    template: 'Attenzione tutte le unità, operazione {CODICE_INTERVENTO} conclusa alle ore {ORA}, bonifica completata, tutte le squadre possono rientrare',
    variabili: ['CODICE_INTERVENTO', 'ORA'],
    esempio: 'Attenzione tutte le unità, operazione Alfa 123 conclusa alle ore 18:45, bonifica completata, tutte le squadre possono rientrare',
  },
];

// Ottieni template per categoria
export const getTemplatesByCategory = (categoria: string): MessageTemplate[] => {
  return TEMPLATES_MESSAGGI.filter((t) => t.categoria === categoria);
};

// Ottieni tutte le categorie
export const getCategories = (): string[] => {
  return [...new Set(TEMPLATES_MESSAGGI.map((t) => t.categoria))];
};

// Compila template con variabili
export const compileTemplate = (template: string, variables: Record<string, string>): string => {
  let result = template;
  for (const [key, value] of Object.entries(variables)) {
    result = result.replace(new RegExp(`{${key}}`, 'g'), value);
  }
  return result;
};

// Formatta orario per comunicazioni
export const formatTimeForRadio = (date: Date): string => {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

// Genera log comunicazione
export const generateCommLog = (
  timestamp: Date,
  from: string,
  to: string,
  message: string
): string => {
  return `[${formatTimeForRadio(timestamp)}] ${from} → ${to}: ${message}`;
};
